import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MiradiUserPreferencesWrapper } from 'app/shared/models/miradi-user-preferences.model';
import { environment } from 'environments/environment';
import { Observable, Observer, of, ReplaySubject, throwError } from 'rxjs';
import { catchError, delay, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { Program, ProgramPermission, ProgramsService, ProjectPermission, User, UserService } from '../api-generated';
import { GlobalBusyService } from '../global-busy/global-busy.service';
import { IntegratedDashboardSessionService } from '../integrated-dashboard/integrated-dashboard-session.service';



@Injectable({
  providedIn: 'root'
})
export class AuthService {

  allProgramsMap: { [key: string]: Program };
  allProgramPermissions: ProgramPermission[];
  currentUser: User;
  currentUserPreferences: MiradiUserPreferencesWrapper;
  currentProgramIdentifier: string;
  currentProgramPermission: ProgramPermission;
  currentProjectPermission: ProjectPermission;

  private inflightRequestTick: number;
  private readonly hadSessionKey = 'miradiHadSession';
  private readonly lastEmailKey = 'miradiLastEmail';
  // A target_link_uri built before returnTarget() existed, or a hand-made link, can wrap
  // the real destination several times over. Bounded so a self-referential one cannot spin.
  private readonly maxTargetDepth = 5;
  private readonly reauthFailedAtKey = 'miradiReauthFailedAt';
  private readonly reauthFailureWindowMs = 10 * 60 * 1000;
  private readonly reauthFailuresKey = 'miradiReauthFailures';
  private readonly reauthPendingKey = 'miradiReauthPendingAt';
  private readonly reauthPendingKindKey = 'miradiReauthPendingKind';
  private readonly reauthRoundTripsKey = 'miradiReauthRoundTrips';
  private readonly rememberedEmailKey = 'miradiRememberedEmail';

  // Raised when a request 401s (the server session lapsed) and when a re-auth
  // round-trip comes back still unauthenticated. The shell shows a modal in
  // response; re-auth only runs on the user's click, so it never redirects an
  // unattended tab. Replayed so a subscriber that initialises after bootstrap
  // still sees an expiry raised during it.
  private sessionExpiredSubject = new ReplaySubject<void>(1);
  readonly sessionExpired$ = this.sessionExpiredSubject.asObservable();

  // Recent consecutive re-auth round-trips the user started that came back
  // unauthenticated. Stale ones are ignored so a blip from earlier doesn't colour a
  // fresh expiry. Round-trips nobody asked for are counted separately: telling a user
  // we couldn't sign them back in, when they never tried, is a false alarm.
  get reauthFailures(): number {
    return this.recentCount(this.reauthFailuresKey);
  }

  // One failure is usually transient (a restart, a blip). Only a repeat means
  // sign-in is actually looping and re-offering it would be futile.
  get signInLooping(): boolean {
    return this.reauthFailures >= 2;
  }

  // Every recent re-auth round-trip that came back unauthenticated, whoever started it.
  // This is the bound on automatic redirects: a guard that keeps sending an anonymous tab
  // to discovery would otherwise bounce it forever, since nothing on that path signs
  // anyone in.
  get reauthBouncing(): boolean {
    return this.recentCount(this.reauthRoundTripsKey) >= 2;
  }

  // Whether this tab ever held an authenticated user. Survives the redirect (and a
  // refresh) so the shell can tell a lapsed session from an anonymous visitor who
  // simply bounced off the IdP - only the first should be told anything expired.
  get hadSession(): boolean {
    return !!sessionStorage.getItem(this.hadSessionKey);
  }

  // The email to re-authenticate with. currentUser is nulled by the very 401 that raises
  // the expiry modal, so it cannot be the source for the re-auth that follows.
  //
  // The per-tab copy wins: it is who *this* tab was. The browser-wide one is the last
  // email signed in anywhere here, which in a second tab belongs to somebody else, and
  // sending their address as the login hint would be wrong. It is only the fallback for a
  // tab that has lost its own copy.
  get lastEmail(): string {
    return sessionStorage.getItem(this.lastEmailKey) || this.rememberedEmail;
  }

  // The last email signed in on this browser, kept across tabs, restarts and logout so the
  // sign-in prompt can offer it. Deliberately not cleared by logout - the prompt carries a
  // "Use a different email" link, which is how a shared browser is cleaned up. Not a
  // credential and not proof of anything; the IdP still authenticates.
  get rememberedEmail(): string {
    try {
      return localStorage.getItem(this.rememberedEmailKey) || '';
    } catch {
      // Private-mode and locked-down browsers deny localStorage. Offering no prefill is a
      // fine outcome; throwing out of a sign-in path is not.
      return '';
    }
  }

  forgetRememberedEmail() {
    try {
      localStorage.removeItem(this.rememberedEmailKey);
    } catch {
      // Nothing was stored if the store is unavailable.
    }
  }

  private rememberEmail(email: string) {
    try {
      localStorage.setItem(this.rememberedEmailKey, email);
    } catch {
      // See rememberedEmail: a denied store costs a prefill, nothing more.
    }
  }

  private recentCount(key: string): number {
    const failedAt = Number(sessionStorage.getItem(this.reauthFailedAtKey) || 0);
    if (!failedAt || Date.now() - failedAt > this.reauthFailureWindowMs) return 0;

    return Number(sessionStorage.getItem(key) || 0);
  }

  // A re-auth round-trip in flight. Windowed like the failure counter: a redirect
  // that never lands back on getSessionUser() would otherwise leave this set for the
  // life of the tab, and an unrelated 401 later would be counted as a bogus failure.
  private get reauthPending(): boolean {
    const pendingAt = Number(sessionStorage.getItem(this.reauthPendingKey) || 0);

    return !!pendingAt && Date.now() - pendingAt <= this.reauthFailureWindowMs;
  }

  // What a round-trip in flight was for, which decides how its outcome reads:
  // 'user'   - the user asked to be signed back in, so coming back anonymous is a failure
  //            they should hear about.
  // 'prompt' - we handed them to the page that takes an email. Coming back anonymous is
  //            exactly where that was going.
  // 'auto'   - a guard redirected on its own. Bounded, but never reported as their failure.
  private markReauthPending(kind: 'auto' | 'prompt' | 'user') {
    sessionStorage.setItem(this.reauthPendingKey, String(Date.now()));
    sessionStorage.setItem(this.reauthPendingKindKey, kind);
  }

  notifySessionExpired() {
    this.sessionExpiredSubject.next();
  }

  constructor(
    private globalBusyService: GlobalBusyService,
    private integratedDashboardSessionService: IntegratedDashboardSessionService,
    private programsService: ProgramsService,
    private router: Router,
    private userService: UserService,
  ) {

  }

  loginOrRegister(endpoint: 'login' | 'register', userSearchArg?: string, returnUri?: string) {
    this.allProgramPermissions = undefined;

    const targetLinkUri = this.returnTarget(returnUri);
    const timeZone = Intl.DateTimeFormat()?.resolvedOptions()?.timeZone;
    if (userSearchArg) this.navigateTo(`${environment.environmentBaseUrl}/${endpoint}?user_search_arg=${encodeURIComponent(userSearchArg)}${timeZone ? '&tz=' + encodeURIComponent(timeZone) : ''}${targetLinkUri ? '&target_link_uri=' + targetLinkUri : ''}`);
    else this.navigateTo(`${environment.environmentBaseUrl}/${endpoint}${targetLinkUri ? '?target_link_uri=' + targetLinkUri : ''}`);
  }

  // The one place the shell leaves the app, and the one place it reads where it is.
  // Routing every sign-in and recovery redirect through them lets those flows be
  // exercised without driving a real browser.
  private navigateTo(url: string) {
    window.location.href = url;
  }

  private currentUrl(): string {
    return window.location.href;
  }

  // The URL to come back to after sign-in, encoded exactly once, or undefined when there
  // is nothing to say.
  //
  // window.location.href may already carry a target_link_uri: discovery bounces the tab
  // back with one and the sign-in prompt then runs from that page. Wrapping the whole URL
  // would nest the parameter on every round-trip, and since the inner value's separators
  // go unescaped the outer one is truncated at them - which is how a lapsed session lands
  // back on the anonymous home page instead of the route it left. Carry the inner target
  // forward instead of wrapping it.
  //
  // searchParams.get() hands back a decoded value, so whatever comes out of here has to be
  // encoded again before it goes into a query string - every return path below does, and
  // exactly once. A target carrying its own query parameters otherwise loses everything
  // after the first '&' to the URL it is embedded in.
  private returnTarget(returnUri?: string): string {
    const target = new URL(this.currentUrl()).searchParams.get('target_link_uri') || returnUri;
    if (!target) return undefined;

    try {
      // Unwrap to the innermost destination. A link that wrapped instead of carrying
      // forward leaves the real route nested, and taking the outer one would land the
      // user on whatever page happened to be in the middle of the chain.
      let url = new URL(target, document.location.origin);
      for (let depth = 0; depth < this.maxTargetDepth; depth++) {
        const nested = url.searchParams.get('target_link_uri');
        if (!nested) break;

        url = new URL(nested, document.location.origin);
      }

      // login_required is a one-shot instruction to open the sign-in prompt. Returning to
      // a URL that still carries it re-opens the prompt on a page they just signed in to.
      url.searchParams.delete('login_required');
      url.searchParams.delete('loginRequired');
      url.searchParams.delete('target_link_uri');

      return encodeURIComponent(url.href);
    } catch {
      // A hand-edited target is the server's problem to reject, not a reason to strand
      // the user: pass it along encoded rather than throwing out of the sign-in redirect.
      return encodeURIComponent(target);
    }
  }

  // Discovery does not reach the IdP: the server answers it with a redirect back to the
  // app carrying login_required=1, which opens the sign-in prompt. So this is a hand-off
  // to email entry, not a sign-in attempt.
  discovery(kind: 'auto' | 'prompt' = 'auto') {
    // Only a tab that previously held a session is re-authenticating. A first-time
    // visitor follows the same discovery redirect but must stay out of the recovery
    // state machine if they return without signing in.
    if (this.hadSession) this.markReauthPending(kind);

    const target = this.returnTarget(this.currentUrl());
    this.navigateTo(`${environment.environmentBaseUrl}/discovery?target_link_uri=${target}`);
  }

  // Re-authenticate a user we already know (e.g. after their session lapsed).
  // Going straight to their IdP via the known email skips the provider-selection
  // screen; when the upstream session is still alive it resolves silently.
  //
  // The email comes from storage, not currentUser: the 401 that raises the expiry modal
  // nulls currentUser before the user can click, so reading it here always fell through to
  // discovery - which never reaches the IdP and cannot sign anyone back in.
  //
  // With no email at all - a tab that held a session before that key existed - there is
  // nothing to re-authenticate with, so hand them to the sign-in prompt instead. That
  // round-trip is marked 'prompt': arriving anonymous is where it was going, and the
  // expiry modal is not dismissible, so re-raising it would cover the one field they need.
  reauthenticate() {
    const email = this.currentUser?.email || this.lastEmail;
    if (!email) {
      this.discovery('prompt');
      return;
    }

    this.markReauthPending('user');
    this.loginOrRegister('login', email, this.currentUrl());
  }

  // Recovery when sign-in keeps looping: an RP-initiated logout ends the stale
  // upstream session (what a private window would have sidestepped), leaving a
  // clean slate to sign in from.
  resetSignIn() {
    this.clearReauthAttempts();
    // Come back with the sign-in prompt showing, so starting over ends somewhere
    // they can actually sign in from rather than on an anonymous page.
    this.logout(`${document.location.origin}${environment.appBaseHref}?login_required=1`);
  }

  // Settle a re-auth that was in flight across the redirect: clear it on success,
  // otherwise count the failure and raise the modal, so a looping user isn't left
  // on an anonymous page with no explanation.
  private settleReauthAttempt(authenticated: boolean) {
    if (authenticated) {
      this.clearReauthAttempts();
      return;
    }

    if (!this.reauthPending) return;

    const kind = sessionStorage.getItem(this.reauthPendingKindKey);
    const roundTrips = this.recentCount(this.reauthRoundTripsKey) + 1;
    const failures = this.reauthFailures + (kind === 'user' ? 1 : 0);

    sessionStorage.removeItem(this.reauthPendingKey);
    sessionStorage.removeItem(this.reauthPendingKindKey);
    sessionStorage.setItem(this.reauthRoundTripsKey, String(roundTrips));
    sessionStorage.setItem(this.reauthFailuresKey, String(failures));
    sessionStorage.setItem(this.reauthFailedAtKey, String(Date.now()));

    // A hand-off to the sign-in prompt landed where it meant to. The modal is not
    // dismissible, so raising it here would cover the email field we just sent them to.
    if (kind !== 'prompt') this.notifySessionExpired();
  }

  private clearReauthAttempts() {
    sessionStorage.removeItem(this.reauthFailedAtKey);
    sessionStorage.removeItem(this.reauthFailuresKey);
    sessionStorage.removeItem(this.reauthPendingKey);
    sessionStorage.removeItem(this.reauthPendingKindKey);
    sessionStorage.removeItem(this.reauthRoundTripsKey);
  }

  logout(returnUri?: string) {
    this.allProgramPermissions = undefined;
    this.currentUser = undefined;
    // Signing out ends the session deliberately, so a later 401 here is a fresh
    // sign-in, not an expiry.
    this.clearReauthAttempts();
    sessionStorage.removeItem(this.hadSessionKey);
    // The per-tab copy goes; the remembered email stays, so the prompt they land on still
    // offers it. "Use a different email" is what clears that.
    sessionStorage.removeItem(this.lastEmailKey);

    const target = returnUri || `${document.location.origin}${environment.appBaseHref}`;
    const url = `${environment.environmentBaseUrl}/logout?target_link_uri=${encodeURIComponent(target)}`;

    // Ending the Miradi session does not end the Tableau one: that is a cookie
    // for another origin, held by this browser, and only this browser can drop
    // it. Without this, the next person to sign in here inherits the previous
    // viewer's Tableau session, and no amount of Miradi-side gating prevents it.
    //
    // The navigation waits for it rather than relying on `keepalive`, which is
    // not proven to carry a cross-site credentialed request. The service
    // completes on failure too, so a Tableau outage cannot strand a logout.
    this.integratedDashboardSessionService.signOut()
    .subscribe(() => this.navigateTo(url));
  }

  completeAccountSetup(): Observable<void> {
    return this.userService.completeAccountSetup()
      .pipe(
        switchMap(() => {
          // No user object returned from 200 response, need to refresh user data separately
          return this.getSessionUser(false).pipe(
            tap(user => {
              this.currentUser = user;
              this.setAwsRumUserContext(user);
            }),
            map(() => void 0)  // Return void
          );
        })
      );
  }

  getSessionUser(canUseCachedValue?: boolean): Observable<User> {
    if (canUseCachedValue && this.currentUser) {
      this.setAwsRumUserContext(this.currentUser);

      return of(this.currentUser)
      .pipe(
        delay(1)
      );
    }

    // if we ever need to query the mod_auth session info directly:
    // this.httpClient.get(environment.environmentBaseUrl + '/redirectUri?info=json')
    return this.userService.getUser()
    .pipe(
      catchError((error: any) => {
        if (error.status !== 0 && error.status !== 401) {
          console.error(error);

          if ((window as any).awsRum) {
            (window as any).awsRum.recordError(error.originalError || error);
          }
        }

        return of(null);
      }),
      mergeMap((response: User) => {
        this.currentUser = response;

        if (response) {
          sessionStorage.setItem(this.hadSessionKey, '1');
          // Kept for the re-auth that a later expiry will offer, when currentUser is gone,
          // and browser-wide so the sign-in prompt can offer it on a later visit.
          if (response.email) {
            sessionStorage.setItem(this.lastEmailKey, response.email);
            this.rememberEmail(response.email);
          }
        }
        this.settleReauthAttempt(!!response);
        this.setAwsRumUserContext(this.currentUser);

        if (this.currentUser) return this.userService.getUserPreferences();
        else return of(null);
      }),
      map((response: any) => {
        this.currentUserPreferences = new MiradiUserPreferencesWrapper(response);

        return this.currentUser;
      })
    );
  }

  private setAwsRumUserContext(user: User) {
    if (!(window as any).awsRum || !(window as any).awsRum.setSessionAttributes) return;

    if (user) {
      // Use user GUID (not email) for privacy-safe tracking, consistent with backend SessionSpanProcessor
      (window as any).awsRum.setSessionAttributes({
        userId: user.identifier,
        organizationId: user.organizationIdentifier,
        email: user.email
      });
    } else {
      (window as any).awsRum.setSessionAttributes({
        userId: null,
        organizationId: null,
        email: null
      });
    }
  }

  getAllPrograms(): Observable<Program[]> {
    if (this.allProgramsMap) {
      return of(Object.values(this.allProgramsMap));
    } else {
      return this.programsService.getPrograms()
      .pipe(
        map((programs: Program[]) => {
          this.allProgramsMap = {};
          for (const p of programs || []) {
            this.allProgramsMap[p.identifier] = p;
          }
          return Object.values(this.allProgramsMap);
        })
      );
    }
  }

  getAllProgramPermissions(): Observable<ProgramPermission[]> {
    if (this.allProgramPermissions) {
      return of(this.allProgramPermissions);
    } else if (!this.inflightRequestTick) {
      this.inflightRequestTick = Date.now();
      return this.userService.getAllProgramPermissions()
      .pipe(
        map((pps: ProgramPermission[]) => {
          this.inflightRequestTick = undefined;

          this.allProgramPermissions = pps;
          return pps;
        })
      );
    } else {
      // a previous request is currently in progress, so try again in a few milliseconds to see if the result has become available
      return new Observable((observer: Observer<any>) => {
        setTimeout(() => {
          this.getAllProgramPermissions()
          .subscribe((response: any) => {
            observer.next(response);
            observer.complete();
          });
        }, 250);
      });
    }
  }

  getProgramPermissions(programIdentifier?: string, force?: boolean): Observable<ProgramPermission> {
    if (
      !force &&
      this.currentProgramPermission &&
      this.currentProgramPermission.programIdentifier === programIdentifier
    ) {
      return of(this.currentProgramPermission).pipe(delay(10));
    } else {
      this.globalBusyService.setBusy(true);
      return this.userService.getProgramPermissions(programIdentifier)
      .pipe(
        map((pp: ProgramPermission) => {
          this.currentProgramPermission = pp;
          this.globalBusyService.setBusy(false);
          return pp;
        })
      );
    }
  }

  getProjectPermissions(projectIdentifier: string, force?: boolean, skipSetBusy?: boolean): Observable<ProjectPermission> {
    if (
      !force &&
      this.currentProjectPermission &&
      this.currentProjectPermission.projectIdentifier === projectIdentifier
    ) {
      return of(this.currentProjectPermission).pipe(delay(10));
    } else {
      if (!skipSetBusy) this.globalBusyService.setBusy(true);
      return this.userService.getProjectPermissions(projectIdentifier)
      .pipe(
        map((pp: ProjectPermission) => {
          this.currentProjectPermission = pp;
          if (!skipSetBusy) this.globalBusyService.setBusy(false);
          return pp;
        })
      );
    }
  }

  setCurrentProgram(programIdentifier: string) {
    this.currentProgramIdentifier = programIdentifier;

    if (this.allProgramPermissions && this.allProgramPermissions.length) {
      this.currentProgramPermission = this.allProgramPermissions
      .find((pp: ProgramPermission) => {
        return pp.programIdentifier === programIdentifier;
      });
    }
  }

}
